import { Suspense, lazy } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ErrorBoundary } from "@/components/layout/ErrorBoundary";
import { PageSpinner } from "@/components/ui/page-spinner";
// Eager (not lazy): this is a tiny resolver that fetches the org's single review
// page and redirects to the editor. Lazy-loading it would blank the content under
// the null Outlet Suspense fallback during its own chunk load (a ~300ms gap)
// before it can render the editor skeleton. Eager keeps the transition seamless.
import ReviewPages from "./pages/ReviewPages";

// Auth shell (provides AuthProvider + OrganizationProvider via Outlet)
const AuthShell = lazy(() => import("./components/layout/AuthShell"));
// Dashboard shell (provides SidebarProvider + AppSidebar + SiteHeader via Outlet)
const DashboardShell = lazy(() => import("./components/layout/DashboardShell"));

// Lazy load all page components for code splitting
const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Contacts = lazy(() => import("./pages/Contacts"));
const Locations = lazy(() => import("./pages/Locations"));
const Templates = lazy(() => import("./pages/Templates"));
const Settings = lazy(() => import("./pages/Settings"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Campaigns = lazy(() => import("./pages/Campaigns"));
const CampaignDetail = lazy(() => import("./pages/CampaignDetail"));
const NewCampaignRedirect = lazy(() => import("./pages/NewCampaignRedirect"));
const AcceptInvite = lazy(() => import("./pages/AcceptInvite"));
const SetupSubscription = lazy(() => import("./pages/SetupSubscription"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const ConfirmEmail = lazy(() => import("./pages/ConfirmEmail"));
const ReviewLanding = lazy(() => import("./pages/ReviewLanding"));
const ReviewLandingBySlug = lazy(() => import("./pages/ReviewLandingBySlug"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const ReviewAnalytics = lazy(() => import("./pages/ReviewAnalytics"));
const TemplateCreateSMS = lazy(() => import("./pages/TemplateCreateSMS"));
const TemplateEdit = lazy(() => import("./pages/TemplateEdit"));
const SMSFlow = lazy(() => import("./pages/SMSFlow"));
const ReviewPageEditorPage = lazy(() => import("./pages/ReviewPageEditor"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const ReviewInbox = lazy(() => import("./pages/ReviewInbox"));
const ReviewSources = lazy(() => import("./pages/ReviewSources"));
const SourceRedirect = lazy(() => import("./pages/SourceRedirect"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <BrowserRouter>
          <ErrorBoundary>
            <Suspense fallback={<PageSpinner />}>
              <Routes>
                {/* Public review routes — fully isolated, no auth providers */}
                <Route path="/review/:token" element={<ReviewLanding />} />
                {/* A review page is reachable only through a location. The
                    page's own slug was retired from the editor on 2026-06-21;
                    the bare /r/:slug route outlived it, serving an unscoped
                    all-branches funnel that the product deliberately does not
                    show. Removed — an unknown slug now 404s. */}
                <Route path="/r/:senderId/:slug" element={<ReviewLandingBySlug />} />

                {/* Public source-attribution links — count the visit then
                    redirect to the target location's review funnel. */}
                <Route path="/s/:senderId/:slug" element={<SourceRedirect />} />
                <Route path="/s/:slug" element={<SourceRedirect />} />

                {/* Public SMS unsubscribe — embedded in outbound-SMS footer */}
                <Route path="/u/:token" element={<Unsubscribe />} />

                {/* Auth context wrapper — no sidebar */}
                <Route element={<AuthShell />}>
                  <Route path="/" element={<Index />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/signin" element={<Navigate to="/auth" replace />} />
                  <Route path="/login" element={<Navigate to="/auth" replace />} />
                  {/* Sign-up is a tab on /auth, not a page of its own, so the
                      most obvious URL a new customer types has to carry the tab
                      with it. Without `?tab=signup` this lands them on the
                      sign-in form, which is the wrong half of the screen for
                      somebody who has no account yet. There is deliberately no
                      /register: nothing in the client links to that word (the
                      API's POST /auth/register is a server route, not a page),
                      and a redirect nobody can reach is a route to maintain for
                      no one. */}
                  <Route path="/signup" element={<Navigate to="/auth?tab=signup" replace />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/reset-password/:token" element={<ResetPassword />} />
                  <Route path="/confirm-email" element={<ConfirmEmail />} />
                  <Route path="/create-organization" element={<Navigate to="/onboarding" replace />} />
                  <Route path="/onboarding" element={<Onboarding />} />
                  <Route path="/setup-subscription" element={<SetupSubscription />} />
                  <Route path="/invite" element={<AcceptInvite />} />
                  <Route path="/invite/:token" element={<AcceptInvite />} />

                  {/* Dashboard routes — sidebar layout */}
                  <Route element={<DashboardShell />}>
                    {/* The dashboard IS the guided checklist until setup is
                        finished, so the checklist has no route of its own any
                        more. This one stays reachable for bookmarks and for any
                        link that still points at the old page. */}
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/getting-started" element={<Navigate to="/dashboard" replace />} />
                    <Route path="/contacts" element={<Contacts />} />
                    <Route path="/locations" element={<Locations />} />
                    <Route path="/templates" element={<Templates />} />
                    <Route path="/templates/new" element={<TemplateCreateSMS />} />
                    <Route path="/templates/new/sms" element={<Navigate to="/templates/new" replace />} />
                    <Route path="/templates/:id/edit" element={<TemplateEdit />} />
                    <Route path="/sms-flow" element={<SMSFlow />} />
                    <Route path="/quick-send" element={<Navigate to="/sms-flow?entry=quick-send" replace />} />
                    <Route path="/campaigns" element={<Campaigns />} />
                    <Route path="/campaigns/new" element={<NewCampaignRedirect />} />
                    <Route path="/campaigns/:id" element={<CampaignDetail />} />
                    {/* Legacy analytics deep-link redirect → unified overview. */}
                    <Route path="/campaigns/:id/analytics" element={<Navigate to=".." replace relative="path" />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/reviews" element={<ReviewInbox />} />
                    <Route path="/reviews/inbox" element={<Navigate to="/reviews" replace />} />
                    <Route path="/reviews/pages" element={<ReviewPages />} />
                    <Route path="/reviews/sources" element={<ReviewSources />} />
                    <Route path="/reviews/analytics" element={<ReviewAnalytics />} />
                    {/* Full-page editor — renders flush/full-height inside the shell
                       (DashboardShell matches EDITOR_PATTERN: hides the header, keeps
                       the nav sidebar visible, locks height to the viewport). */}
                    <Route path="/reviews/pages/:id/edit" element={<ReviewPageEditorPage />} />
                    {/* MVP single-page model: creating a new page is not allowed; redirect to the org's page. */}
                    <Route path="/reviews/pages/new" element={<Navigate to="/reviews/pages" replace />} />
                  </Route>

                  {/* Catch-all 404 — reachable from both authed and unauthed contexts.
                     Lives at the AuthShell level (no sidebar) so logged-out users see
                     the page instead of a redirect to /auth. */}
                  <Route path="*" element={<NotFound homeHref="/" />} />
                </Route>
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
